import styles from "./app-shell.module.scss";
import { AppShell as MantineAppShell, rem, ScrollArea } from "@mantine/core";
import {
    PADDING_TOP_PRESETS,
    type CustomAppShellFactory,
    type CustomAppShellSecondaryNavbarFactory,
    type CustomAppShellMainFactory,
    type IProps,
    type ISecondaryNavbarProps,
    type IAppShellMainProps,
    type PaddingTopPreset,
} from "./app-shell.types";
import { componentWrapper } from "../../utilities";
import { Box } from "../box";
import { createContext, MutableRefObject, useCallback, useContext, useLayoutEffect, useState } from "react";

interface IAppShellContext {
    secondaryNavbarRef: MutableRefObject<HTMLElement | null>;
    registerSecondaryNavbar: (ref: HTMLElement | null) => void;
}

const AppShellContext = createContext<IAppShellContext>({
    registerSecondaryNavbar: () => void 0,
    secondaryNavbarRef: { current: null },
});

const AppShell = componentWrapper<CustomAppShellFactory, IProps>(
    "AppShell",
    ({ secondaryNavbar, ...otherProps }, ref) => {
        const [secondaryNavbarRef, setSecondaryNavbarRef] = useState<MutableRefObject<HTMLElement | null>>({
            current: null,
        });

        const registerSecondaryNavbar = useCallback((ref: HTMLElement | null) => {
            setSecondaryNavbarRef({ current: ref });
        }, []);

        return (
            <AppShellContext.Provider value={{ secondaryNavbarRef, registerSecondaryNavbar }}>
                <MantineAppShell
                    withBorder={false}
                    ref={ref}
                    data-has-secondary-navbar={!!secondaryNavbar && secondaryNavbarRef.current !== null}
                    classNames={{ root: styles["container"], main: styles["main"], header: styles["header"] }}
                    __vars={{ "--app-shell-secondary-navbar-width": rem(secondaryNavbar?.width || 0) }}
                    {...otherProps}
                />
            </AppShellContext.Provider>
        );
    }
);

const AppShellSecondaryNavbar = componentWrapper<CustomAppShellSecondaryNavbarFactory, ISecondaryNavbarProps>(
    "AppShellSecondaryNavbar",
    ({ children, disableScroll, ...otherProps }, ref) => {
        const { secondaryNavbarRef, registerSecondaryNavbar } = useContext(AppShellContext);

        useLayoutEffect(() => {
            return () => {
                registerSecondaryNavbar(null);
            };
        }, [registerSecondaryNavbar]);

        return (
            <Box
                component="nav"
                ref={(ref) => {
                    if (ref !== null && secondaryNavbarRef.current === null) {
                        registerSecondaryNavbar(ref);
                    }
                }}
                className={styles["secondaryNavbar"]}
                data-disable-scroll={disableScroll}
                // When scroll is enabled, the scrollview is who adds the padding. When disabling it we need to do it manually.
                pr={disableScroll ? "md" : undefined}
                {...otherProps}
            >
                {disableScroll ? (
                    children
                ) : (
                    <ScrollArea type="hover" offsetScrollbars="y">
                        {children}
                    </ScrollArea>
                )}
            </Box>
        );
    }
);

const paddingTopPresetSet: ReadonlySet<PaddingTopPreset> = new Set(PADDING_TOP_PRESETS);

/**
 * Custom AppShell.Main wrapper that supports padding-top presets.
 * Preset values ("default", "header", "none") are applied via data attributes
 * and styled in SCSS. Non-preset values pass through to Mantine.
 */
const AppShellMain = componentWrapper<CustomAppShellMainFactory, IAppShellMainProps>(
    "AppShellMain",
    ({ pt, ...otherProps }, ref) => {
        const isPreset = typeof pt === "string" && paddingTopPresetSet.has(pt as PaddingTopPreset);

        return <MantineAppShell.Main ref={ref} {...otherProps} {...(isPreset ? { "data-padding-top": pt } : { pt })} />;
    }
);

AppShell.Aside = MantineAppShell.Aside;
AppShell.Footer = MantineAppShell.Footer;
AppShell.Header = MantineAppShell.Header;
AppShell.Main = AppShellMain;
AppShell.Navbar = MantineAppShell.Navbar;
AppShell.SecondaryNavbar = AppShellSecondaryNavbar;
AppShell.Section = MantineAppShell.Section;

export { AppShell, AppShellSecondaryNavbar, AppShellMain };
